import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useFeature } from 'flagged';
import PropTypes from 'prop-types';
import MenuButton from '@/components/common/MenuButton';
import FeatureFlags from '@/constants/system/FeatureFlags';
import useMobileView from '@/hooks/ui/useMobileView';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
import ConstituentPropType from '@/lib/types/ConstituentPropType';
import LifecycleColumnForm from './LifecycleColumnForm';

function LifecycleColumnHeader({
  className,
  columnId,
  columnDescription,
  columnTitle,
  constituents,
  filters,
  onCancelUpdate,
  onRemove,
  onUpdate,
  query,
  setQuery,
  setSelectedItems,
  total,
}) {
  const { bulkEditColumns } = useFeature(
    FeatureFlags.FEATURE_FLAG_BULK_EDIT_COLUMNS,
  );
  const [isEditActive, setIsEditActive] = React.useState(!columnId);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { isSuperAdmin, isOrgAdmin } = useCurrentUser();
  const isMobile = useMobileView();
  const menuId = `${columnId}-menu`;

  const canEditBoard = isSuperAdmin || isOrgAdmin;

  const openMenu = (e) => setAnchorEl(e.target);
  const closeMenu = () => setAnchorEl(null);

  const openEdit = () => {
    closeMenu();
    setIsEditActive(true);
  };
  const closeEdit = () => {
    setIsEditActive(false);
  };
  const setBulkQuery = () => {
    closeMenu();
    setQuery([...query, filters]);
    setSelectedItems((previousState) => {
      if (previousState.includes(constituents[0].constituentId)) {
        const updatedItems = previousState.filter(
          (id) => !constituents.some((cnx) => cnx.constituentId === id),
        );
        const removedLength = previousState.filter((id) =>
          constituents.some((cnx) => cnx.constituentId === id),
        ).length;

        updatedItems.length = previousState.length - removedLength;
        return updatedItems;
      }
      const updatedItems = [
        ...new Set(
          previousState.concat(
            constituents.map(({ constituentId }) => constituentId),
          ),
        ),
      ];

      updatedItems.length = previousState.length + total;
      return updatedItems;
    });
  };

  const items = [
    {
      label: translate('BOARD_EDIT_COLUMN_INFO'),
      onClick: openEdit,
    },
    bulkEditColumns && {
      label: translate('BOARD_SELECT_ALL_CONSTITUENTS_IN_COLUMN'),
      onClick: setBulkQuery,
    },
    {
      label: translate('BOARD_REMOVE_COLUMN'),
      onClick: onRemove,
      className: 'text-error-main',
    },
  ].filter(Boolean);

  const title = React.useMemo(
    () => (
      <h2 className="mr-2 type-body2">{`${columnTitle} (${total ?? '-'})`}</h2>
    ),
    [columnTitle, total],
  );

  return (
    <div className={className}>
      {!canEditBoard && title}
      {canEditBoard &&
        (isEditActive ? (
          <LifecycleColumnForm
            columnDescription={columnDescription}
            columnTitle={columnTitle}
            onCancelUpdate={onCancelUpdate}
            onClose={closeEdit}
            onSubmit={onUpdate}
          />
        ) : (
          <>
            <Tooltip
              title={translate(
                isMobile ? 'BOARD_TAP_TO_EDIT' : 'BOARD_CLICK_TO_EDIT',
              )}
              enterDelay={1000}
            >
              <button
                type="button"
                onClick={openEdit}
                aria-label={translate('EDIT')}
                className="text-left"
                data-testid="lifecycle-edit-info"
              >
                {title}
              </button>
            </Tooltip>
            <MenuButton
              className="-my-3 ml-auto -mr-3"
              onClick={openMenu}
              aria-controls={anchorEl ? menuId : undefined}
              aria-haspopup="true"
              data-testid="lifecycle-edit-menu-btn"
            />
            <Menu
              id={menuId}
              onClose={closeMenu}
              open={!!anchorEl}
              anchorEl={anchorEl}
              data-testid="lifecycle-edit-menu"
            >
              {items.map(({ label, ...rest }) => (
                <MenuItem key={label} {...rest}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </>
        ))}
    </div>
  );
}

LifecycleColumnHeader.propTypes = {
  className: PropTypes.string,
  columnDescription: PropTypes.string,
  columnId: PropTypes.string,
  columnTitle: PropTypes.string,
  constituents: PropTypes.arrayOf(ConstituentPropType),
  filters: PropTypes.shape({
    columnId: PropTypes.string,
    queryType: PropTypes.string,
  }).isRequired,
  onCancelUpdate: PropTypes.func,
  onRemove: PropTypes.func,
  onUpdate: PropTypes.func,
  query: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setQuery: PropTypes.func.isRequired,
  setSelectedItems: PropTypes.func.isRequired,
  total: PropTypes.number,
};

LifecycleColumnHeader.defaultProps = {
  className: '',
  columnDescription: null,
  columnTitle: null,
  columnId: null,
  constituents: [],
  onCancelUpdate: () => {},
  onRemove: () => {},
  onUpdate: () => {},
  total: null,
};

export default LifecycleColumnHeader;
